import type { CompanyAccountModelDto } from '@treasury/api/channel';
import { AchException } from '@treasury/domain/arp';
import { PayOrReturnChoice } from '@treasury/domain/channel/mappings/ach';
import { TmContainer } from '@treasury/presentation';
import '@treasury/presentation/components/layout/tm-section';
import '@treasury/presentation/components/tm-badge';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-list-header';
// eslint-disable-next-line no-duplicate-imports
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import { columnBodyRenderer } from '@vaadin/grid/lit.js';
import '@vaadin/grid/vaadin-grid-selection-column.js';
import '@vaadin/grid/vaadin-grid-sort-column.js';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { filterTableResults } from '../../../../utilities/table-result-filter';
import '../../components/ach-decision-toggle';
import './ach-exceptions-filter-sheet';

export const tagName = 'ach-exceptions-table';
@customElement(tagName)
export class AchExceptionsTable extends TmContainer {
    @property({ type: Array })
    public achExceptions: AchException[] = [];

    @property({ type: Boolean })
    public hasFooterMessage = false;

    @property({ type: Boolean })
    public decisioningDisabled = false;

    @property({ type: String })
    public actionName = 'Review';

    @property({ type: Boolean })
    public hideTotals = false;

    @property({ type: Boolean })
    public actionLoading = false;

    @property({ type: Array })
    public filteredItems: AchException[] = [];

    @property({ type: Boolean, reflect: true, attribute: 'readonly' })
    public readonly = false;

    @property({ type: Array })
    private accounts: CompanyAccountModelDto[] = [];

    @property({ type: Array })
    private secCodes: { secCode?: string; description?: string }[] = [];

    @state()
    private filterSheetOpen = false;

    @state()
    private showSelectAllOptions = false;

    @state()
    private isItemListOverflowing = false;

    @state()
    private isTotalsClipped = false;

    private _disconnectObserver?: () => void;

    private _disconnectTotalsObserver?: () => void;

    private get measurements() {
        return {
            footerCounterPlacement: this.hasFooterMessage
                ? 'w-full paper h-9 fixed bottom-[10.0rem]'
                : 'w-full paper h-9 fixed bottom-[7.2rem]' /* Total | Undecisioned | etc */,
        };
    }

    private readonly tabTitle = 'ACH Exceptions';

    private readonly fieldsToFilter = [
        'name',
        'amount',
        'accountName',
        'accountNumber',
        'achCompanyName',
    ];

    private get decisionedExceptions() {
        return this.achExceptions.filter(ae => ae.decisionChoice !== PayOrReturnChoice.Unselected);
    }

    private get hasItems() {
        return this.achExceptions?.length > 0;
    }

    private get totalAchExceptions() {
        return this.achExceptions.length || 0;
    }

    public disconnectedCallback() {
        this._disconnectObserver?.();
        super.disconnectedCallback();
    }

    public render() {
        return html` <tm-list-header
                .headerTitle=${this.tabTitle}
                .filteringEnabled=${!this.readonly && !this.decisioningDisabled}
                .filterButton=${true}
                @filterButtonClick=${() => (this.filterSheetOpen = true)}
                @search-term-change=${(e: TmSearchTermChangeEvent) => {
                    this.filteredItems = filterTableResults(
                        e,
                        this.achExceptions,
                        this.fieldsToFilter
                    ) as AchException[];
                }}
            >
            </tm-list-header>
            <div id="grid-wrapper" class="overflow-y-auto flex-1">${this.renderTable()}</div>
            <div
                id="actions-wrapper"
                class="flex justify-start w-full paper pl-3 fixed bottom-[4.5rem] h-12"
            >
                ${this.renderActions()}
            </div>

            <slot></slot>
            ${this.renderFilterSheet()}`;
    }

    protected firstUpdated() {
        const container = this.renderRoot.querySelector('#grid-wrapper');
        if (!container) {
            return;
        }
        this.observeVaadinGridOverflowChanges(container);
    }

    protected observeVaadinGridOverflowChanges(container: Element) {
        const gridObserver = new MutationObserver(() => {
            const grid = container.querySelector('vaadin-grid');
            if (grid) {
                gridObserver.disconnect();

                const updateOverflowStatus = () => {
                    // eslint-disable-next-line no-restricted-syntax
                    this.isItemListOverflowing = grid.getAttribute('overflow') !== null;
                };
                updateOverflowStatus();

                const overflowObserver = new MutationObserver(updateOverflowStatus);
                overflowObserver.observe(grid, {
                    attributes: true,
                    attributeFilter: ['overflow'],
                });
                this._disconnectObserver = () => overflowObserver.disconnect();
                this.scheduleCheckForClippedTotals();
            }
        });
        gridObserver.observe(container, { childList: true, subtree: true });
    }

    protected scheduleCheckForClippedTotals() {
        requestAnimationFrame(() => this.checkIfTotalsAreClipped());
    }

    protected checkIfTotalsAreClipped() {
        // IMPORTANT! The #actions-wrapper must have a fixed height (e.g., h-12) or sticky rendering logic will break.
        const buttons = this.renderRoot.querySelector('#actions-wrapper');
        const totals = this.renderRoot.querySelector('#totals-under-list');
        if (!totals || !buttons) {
            return;
        }
        const totalsRect = totals.getBoundingClientRect();
        const buttonsRect = buttons.getBoundingClientRect();
        this.isTotalsClipped = totalsRect.bottom > buttonsRect.top;
    }

    protected pinTotalsToBottom(): boolean {
        return this.isItemListOverflowing || this.isTotalsClipped;
    }

    protected updated(changedProperties: Map<string | number | symbol, unknown>): void {
        super.firstUpdated(changedProperties);
        if (changedProperties.has('loading') || changedProperties.has('achExceptions')) {
            this.filteredItems = this.achExceptions;
        }
    }

    private toggleShowSelectAllOptions() {
        this.showSelectAllOptions = !this.showSelectAllOptions;
    }

    private getAggregateByDecision(decision: PayOrReturnChoice) {
        if (!this.achExceptions.length) {
            return 0;
        }
        return this.achExceptions.filter(e => e.decisionChoice === decision).length;
    }

    private decisionAll(decision: PayOrReturnChoice) {
        this.dispatchEvent(new CustomEvent('decisionAll', { detail: decision }));
    }

    private cellPartNameGenerator(col: HTMLElement, val: { item: AchException }) {
        const border = col.classList.contains('decision-toggle') ? ' decision-toggle' : '';
        const readonly = this.readonly ? ' read-only' : '';
        return val.item.decisionChoice ? `modified${border}${readonly}` : `${border}${readonly}`;
    }

    private renderTotalsInFooter() {
        return html`
            <div
                id="totals-in-footer"
                class="flex items-end justify-end border-t grid-footer ${this.measurements
                    .footerCounterPlacement}"
            >
                ${this.renderRunningTotals()}
            </div>
        `;
    }

    private renderTotalsUnderList() {
        return html`<div id="totals-under-list" class="flex items-end justify-end">
            ${this.renderRunningTotals()}
        </div> `;
    }

    private renderRunningTotals() {
        return html` <div class="flex items-center h-full pr-6">
            <div class="px-1 flex items-center total-count text-sm">
                <tm-badge>${this.totalAchExceptions}</tm-badge>
                <span class="table-footer-label p-1 text-xs text-secondary">Total</span>
            </div>
            <div class="px-1 flex items-center total-count text-sm">
                <tm-badge>${this.getAggregateByDecision(PayOrReturnChoice.Unselected)} </tm-badge>
                <span class="table-footer-label p-1 text-xs text-secondary">Undecisioned</span>
            </div>
            <div class="px-1 flex items-center pay-total text-sm">
                <tm-badge
                    ><div>${this.getAggregateByDecision(PayOrReturnChoice.Pay)}</div></tm-badge
                >
                <span class="table-footer-label p-1 text-xs text-secondary">Pay</span>
            </div>
            <div class="px-1 flex items-center return-total text-sm">
                <tm-badge
                    ><div>${this.getAggregateByDecision(PayOrReturnChoice.Return)}</div></tm-badge
                >
                <span class="table-footer-label p-1 text-xs text-secondary">Return</span>
            </div>
        </div>`;
    }

    private renderTable() {
        if (this.loading) {
            return html`<div class="text-center empty-state py-16">
                <tm-loader card></tm-loader>
            </div>`;
        }
        if (!this.hasItems) {
            return html`<div class="w-full px-4 py-8 text-center text-sm text-secondary">
                No exceptions found
            </div> `;
        }

        const cellPartNameGen = this.cellPartNameGenerator.bind(this);
        return html`
            <vaadin-grid
                all-rows-visible
                .items=${this.filteredItems}
                .cellPartNameGenerator=${cellPartNameGen}
                class=${classMap({
                    'w-full': true,
                    'h-full': true,
                    'mb-36': !this.hasFooterMessage && this.pinTotalsToBottom(),
                    'mb-60': this.hasFooterMessage && this.pinTotalsToBottom(),
                    paper: true,
                    readonly: !!this.readonly,
                })}
            >
                <vaadin-grid-column
                    class="decision-toggle"
                    width="auto"
                    flex-grow="0"
                    ${columnBodyRenderer(
                        (item: AchException) =>
                            html`<ach-decision-toggle
                                class="h-full"
                                .exception=${item}
                                @change=${() => {
                                    this.dispatchEvent(new CustomEvent('decision'));
                                    this.requestUpdate();
                                }}
                                ?readonly=${this.readonly}
                            ></ach-decision-toggle>`,
                        []
                    )}
                ></vaadin-grid-column>
                <vaadin-grid-column
                    ${columnBodyRenderer(
                        (exception: AchException) => html`
                            <div class="w-full h-full flex items-center justify-between">
                                <div
                                    class="flex flex-col"
                                    @click=${() => {
                                        this.dispatchEvent(
                                            new CustomEvent('selected', { detail: exception })
                                        );
                                    }}
                                    @keyup=${(e: KeyboardEvent) => {
                                        if (e.key === 'Enter') {
                                            this.dispatchEvent(
                                                new CustomEvent('selected', {
                                                    detail: exception,
                                                })
                                            );
                                        }
                                    }}
                                >
                                    <span class="font-medium">${exception.accountNumber}</span>
                                    <span class="text-sm font-light "
                                        >${exception.achCompanyName}</span
                                    >
                                </div>
                                <div
                                    class="flex flex-col"
                                    @click=${() => {
                                        this.dispatchEvent(
                                            new CustomEvent('selected', { detail: exception })
                                        );
                                    }}
                                    @keyup=${(e: KeyboardEvent) => {
                                        if (e.key === 'Enter') {
                                            this.dispatchEvent(
                                                new CustomEvent('selected', {
                                                    detail: exception,
                                                })
                                            );
                                        }
                                    }}
                                >
                                    <span class="pr-2 mr-2 font-medium"
                                        >${new Intl.NumberFormat('en-US', {
                                            style: 'currency',
                                            currency: 'USD',
                                        }).format(exception.amount ?? 0)}</span
                                    >
                                </div>
                            </div>
                        `
                    )}
                >
                </vaadin-grid-column>
            </vaadin-grid>
            ${this.renderTotals()}
        `;
    }

    private renderTotals() {
        if (this.pinTotalsToBottom()) {
            return this.renderTotalsInFooter();
        }
        return this.renderTotalsUnderList();
    }

    private renderActions() {
        if (this.readonly) {
            return nothing;
        }
        if (!this.hasItems) {
            return nothing;
        }
        if (this.loading) {
            return nothing;
        }
        if (!this.showSelectAllOptions) {
            return html`<tm-button importance="secondary" @click=${this.toggleShowSelectAllOptions}>
                Select All
            </tm-button>`;
        }
        return html`<div class="actions">
            <tm-button
                class="mr-2"
                @click=${() => {
                    this.toggleShowSelectAllOptions();
                    this.dispatchEvent(new CustomEvent('clearAll'));
                }}
            >
                Cancel
            </tm-button>
            <tm-button
                class="mr-2"
                success
                importance="primary"
                @click=${() => {
                    this.toggleShowSelectAllOptions();
                    this.decisionAll(PayOrReturnChoice.Pay);
                }}
            >
                Pay All
            </tm-button>
            <tm-button
                class="mr-2"
                error
                importance="primary"
                @click=${() => {
                    this.toggleShowSelectAllOptions();
                    this.decisionAll(PayOrReturnChoice.Return);
                }}
            >
                Return All
            </tm-button>
        </div>`;
    }

    private renderFilterSheet() {
        if (!this.accounts.length || !this.secCodes.length) {
            return nothing;
        }
        return html`<ach-exceptions-filter-sheet
            .open=${this.filterSheetOpen}
            .accounts=${this.accounts}
            .secCodes=${this.secCodes}
            @close=${() => {
                this.filterSheetOpen = false;
            }}
        ></ach-exceptions-filter-sheet>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    --lumo-space-xs: 3px;
                    --lumo-space-m: 0;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                }
                h3 {
                    margin-top: 10px;
                    color: var(--header-text-color);
                }
                .empty-state {
                    color: var(--header-text-color);
                }
                #no-exceptions {
                    margin: auto;
                }
                .max-w-100 {
                    max-width: 150px;
                }
                vaadin-grid-cell-content {
                    padding: 0;
                    height: 100%;
                    display: flex;
                    align-items: center;
                }
                vaadin-grid::part(modified) {
                    background-color: var(--container-page);
                }
                vaadin-grid::part(decision-toggle) {
                    padding-right: 1rem;
                }
                vaadin-grid::part(read-only) {
                    background-color: var(--container-primary);
                }
                vaadin-grid::part(cell) {
                    height: 4rem;
                }
                vaadin-grid {
                    max-height: calc(var(--below-navbar-view-height) - 202px);
                }
                vaadin-grid::part(first-column-cell) {
                    min-width: 50px;
                }
                vaadin-grid::part(last-row) {
                    border-bottom: 1px solid var(--divider-primary); /* Add stripe after last row */
                }
                .paper {
                    background-color: var(--container-primary);
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchExceptionsTable;
    }
}
